import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Phone,
  Mail,
  Users,
  Package,
  Loader2,
  Trash2,
  Copy,
  MessageSquare,
  CreditCard,
  Plus,
  Music,
  Camera,
  Video,
  Wine,
  Cookie,
  UtensilsCrossed,
  FileText,
  Edit,
  Building,
  History,
  AlertTriangle,
  Minus
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API = RUNTIME_BACKEND_URL;

export default function SalonCustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [agreementSummary, setAgreementSummary] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showDebtDialog, setShowDebtDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [venues, setVenues] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_type: 'cash',
    notes: ''
  });
  const [debtForm, setDebtForm] = useState({
    amount: '',
    notes: ''
  });
  const [editForm, setEditForm] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingDebt, setSavingDebt] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadCustomer();
    loadAgreementSummary();
    loadVenues();
  }, [customerId]);

  const loadVenues = async () => {
    try {
      const res = await fetch(`${API}/api/salon/venues`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVenues(data);
      }
    } catch (error) {
      console.error('Venues load error:', error);
    }
  };

  const loadCustomer = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/salon/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomer(data);
      } else {
        toast.error('Müşteri bulunamadı');
        navigate('/salon/musteriler');
      }
    } catch (error) {
      toast.error('Müşteri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadAgreementSummary = async () => {
    try {
      const res = await fetch(`${API}/api/salon/customers/${customerId}/agreement-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAgreementSummary(data);
      }
    } catch (error) {
      console.error('Agreement summary error:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu müşteriyi silmek istediğinize emin misiniz?')) return;

    try {
      const res = await fetch(`${API}/api/salon/customers/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Silinemedi');

      toast.success('Müşteri silindi');
      navigate('/salon/musteriler');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCopyMessage = () => {
    if (agreementSummary?.message_template) {
      navigator.clipboard.writeText(agreementSummary.message_template);
      toast.success('Mesaj kopyalandı! WhatsApp veya SMS ile gönderebilirsiniz.');
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || paymentForm.amount <= 0) {
      toast.error('Geçerli bir tutar girin');
      return;
    }

    setSavingPayment(true);
    try {
      const res = await fetch(
        `${API}/api/salon/customers/${customerId}/payments?amount=${paymentForm.amount}&payment_type=${paymentForm.payment_type}&notes=${encodeURIComponent(paymentForm.notes)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!res.ok) throw new Error('Ödeme eklenemedi');

      toast.success('Ödeme eklendi');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: '', payment_type: 'cash', notes: '' });
      loadCustomer();
      loadAgreementSummary();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPayment(false);
    }
  };

  const handleAddDebt = async () => {
    if (!debtForm.amount || debtForm.amount <= 0) {
      toast.error('Geçerli bir tutar girin');
      return;
    }
    if (!debtForm.notes || debtForm.notes.trim().length < 3) {
      toast.error('Borç eklerken açıklama/not zorunludur (en az 3 karakter)');
      return;
    }

    setSavingDebt(true);
    try {
      const res = await fetch(
        `${API}/api/salon/customers/${customerId}/debts?amount=${debtForm.amount}&notes=${encodeURIComponent(debtForm.notes)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Borç eklenemedi');
      }

      toast.success('Borç eklendi');
      setShowDebtDialog(false);
      setDebtForm({ amount: '', notes: '' });
      loadCustomer();
      loadAgreementSummary();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingDebt(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${API}/api/salon/customers/${customerId}/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Logs load error:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const openLogsDialog = () => {
    setShowLogsDialog(true);
    loadLogs();
  };

  const openEditDialog = () => {
    setEditForm({
      venue_id: customer.venue_id || '',
      venue_name: customer.venue_name || '',
      event_date: customer.event_date || '',
      event_time_start: customer.event_time_start || '18:00',
      event_time_end: customer.event_time_end || '23:00',
      bride_name: customer.bride_name || '',
      groom_name: customer.groom_name || '',
      phone1: customer.phone1 || customer.bride_phone || '',
      phone2: customer.phone2 || customer.groom_phone || '',
      email: customer.email || '',
      guest_count: customer.guest_count || '',
      has_orchestra: customer.has_orchestra || false,
      has_camera: customer.has_camera || false,
      has_photography: customer.has_photography || false,
      has_drinks: customer.has_drinks || false,
      has_snacks: customer.has_snacks || false,
      has_food: customer.has_food || false,
      custom_services: customer.custom_services || '',
      total_price: customer.total_price || 0,
      deposit_amount: customer.deposit_amount || 0,
      notes: customer.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.bride_name || !editForm.groom_name || !editForm.phone1) {
      toast.error('Zorunlu alanları doldurun');
      return;
    }

    setSavingEdit(true);
    try {
      const res = await fetch(`${API}/api/salon/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...editForm,
          guest_count: editForm.guest_count ? parseInt(editForm.guest_count) : null
        })
      });

      if (!res.ok) throw new Error('Güncellenemedi');

      toast.success('Müşteri güncellendi');
      setShowEditDialog(false);
      loadCustomer();
      loadAgreementSummary();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!customer) return null;

  const services = [];
  if (customer.has_orchestra) services.push({ icon: Music, name: 'Orkestra' });
  if (customer.has_camera) services.push({ icon: Video, name: 'Kamera' });
  if (customer.has_photography) services.push({ icon: Camera, name: 'Fotoğraf' });
  if (customer.has_drinks) services.push({ icon: Wine, name: 'İçecek' });
  if (customer.has_snacks) services.push({ icon: Cookie, name: 'Çerez' });
  if (customer.has_food) services.push({ icon: UtensilsCrossed, name: 'Yemek' });

  return (
    <div className="space-y-6" data-testid="salon-customer-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/salon/musteriler')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {customer.bride_name} & {customer.groom_name}
          </h1>
          <p className="text-muted-foreground">
            {formatDate(customer.event_date)}
            {customer.venue_name && <span className="ml-2 text-primary">• {customer.venue_name}</span>}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={openEditDialog} data-testid="edit-customer-btn">
          <Edit className="h-4 w-4" />
        </Button>
        <Button variant="destructive" size="icon" onClick={handleDelete} data-testid="delete-customer-btn">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Müşteri Bilgileri */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Müşteri Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Gelin</p>
                <p className="font-medium">{customer.bride_name}</p>
                <a href={`tel:${customer.phone1 || customer.bride_phone}`} className="text-sm text-primary flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {customer.phone1 || customer.bride_phone}
                </a>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Damat</p>
                <p className="font-medium">{customer.groom_name}</p>
                {(customer.phone2 || customer.groom_phone) && (
                  <a href={`tel:${customer.phone2 || customer.groom_phone}`} className="text-sm text-primary flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {customer.phone2 || customer.groom_phone}
                  </a>
                )}
              </div>
            </div>
            {customer.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {customer.email}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{formatDate(customer.event_date)}</span>
              {customer.event_time_start && (
                <span className="text-muted-foreground">
                  {customer.event_time_start} - {customer.event_time_end}
                </span>
              )}
            </div>
            {customer.guest_count && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{customer.guest_count} Davetli</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paket & Hizmetler */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Paket & Hizmetler
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {customer.package && (
              <div className="p-3 bg-primary/5 rounded-lg">
                <p className="font-medium">{customer.package.name}</p>
                <p className="text-sm text-muted-foreground">Hazır Paket</p>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-muted rounded">
                <p className="text-lg font-bold">{customer.capacity || '-'}</p>
                <p className="text-xs text-muted-foreground">Kişilik</p>
              </div>
              <div className="p-2 bg-muted rounded">
                <p className="text-lg font-bold">{customer.table_count || '-'}</p>
                <p className="text-xs text-muted-foreground">Masa</p>
              </div>
              <div className="p-2 bg-muted rounded">
                <p className="text-lg font-bold">{customer.chair_count || '-'}</p>
                <p className="text-xs text-muted-foreground">Sandalye</p>
              </div>
            </div>

            {services.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {services.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-full text-xs">
                    <s.icon className="h-3 w-3" /> {s.name}
                  </span>
                ))}
              </div>
            )}

            {(customer.custom_services || customer.additional_services) && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Özel İçerik / Ek Hizmetler:</p>
                <p className="text-sm">{customer.custom_services || customer.additional_services}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ödeme Durumu */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Ödeme Durumu
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={openLogsDialog}>
                <History className="h-4 w-4 mr-1" /> Log Kayıtları
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDebtDialog(true)}>
                <Minus className="h-4 w-4 mr-1" /> Borç Ekle
              </Button>
              <Button size="sm" onClick={() => setShowPaymentDialog(true)}>
                <Plus className="h-4 w-4 mr-1" /> Ödeme Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Toplam Tutar</p>
                <p className="text-lg font-bold">{formatCurrency(customer.total_price)}</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Ödenen</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(customer.total_paid)}</p>
              </div>
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Kalan</p>
                <p className="text-lg font-bold text-orange-600">{formatCurrency(customer.remaining_amount)}</p>
              </div>
            </div>

            {/* Ödeme Geçmişi */}
            <div className="space-y-2">
              <p className="text-sm font-medium border-b pb-2">Ödeme Geçmişi</p>
              
              {/* Kapora */}
              {customer.deposit_amount > 0 && (
                <div className="flex justify-between items-start p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-blue-700 dark:text-blue-400">Kapora</span>
                      <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded">Başlangıç</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600 mt-1">{formatCurrency(customer.deposit_amount)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDateTime(customer.created_at)}
                    </p>
                  </div>
                </div>
              )}

              {/* Diğer Ödemeler */}
              {customer.payments && customer.payments.length > 0 ? (
                customer.payments.map((p, index) => (
                  <div key={p.id} className={`flex justify-between items-start p-3 rounded-lg border ${
                    p.is_debt || p.amount < 0 
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                      : 'bg-muted/50'
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {p.is_debt || p.amount < 0 ? (
                          <>
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            <span className="font-semibold text-red-600">Borç Eklendi</span>
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">Ödeme #{index + 1}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              p.payment_type === 'cash' ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300' :
                              p.payment_type === 'credit_card' ? 'bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-300' :
                              'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-300'
                            }`}>
                              {p.payment_type === 'cash' ? 'Nakit' : p.payment_type === 'credit_card' ? 'Kredi Kartı' : 'Havale/EFT'}
                            </span>
                          </>
                        )}
                      </div>
                      <p className={`text-lg font-bold mt-1 ${p.is_debt || p.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {p.is_debt || p.amount < 0 ? `-${formatCurrency(Math.abs(p.amount))}` : formatCurrency(p.amount)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDateTime(p.payment_date)}
                      </p>
                      {p.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          Not: {p.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                !customer.deposit_amount && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Henüz ödeme kaydı yok
                  </p>
                )
              )}
            </div>
          </CardContent>
        </Card>

        {/* Anlaşma Özeti & Mesaj */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Anlaşma Özeti
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg text-sm whitespace-pre-wrap font-mono">
              {agreementSummary?.message_template || 'Yükleniyor...'}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCopyMessage} className="flex-1">
                <Copy className="h-4 w-4 mr-2" />
                Mesajı Kopyala
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(`https://wa.me/${customer.bride_phone?.replace(/\D/g, '')}`, '_blank')}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Kopyalayıp WhatsApp veya SMS ile gönderebilirsiniz.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Notlar */}
      {customer.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notlar</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{customer.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Ödeme Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ödeme Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tutar (TL)</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                onFocus={(e) => e.target.select()}
                placeholder="Tutar girin"
              />
            </div>
            <div className="space-y-2">
              <Label>Ödeme Tipi</Label>
              <Select
                value={paymentForm.payment_type}
                onValueChange={(value) => setPaymentForm({...paymentForm, payment_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Ödeme tipi seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                  <SelectItem value="bank_transfer">Havale/EFT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Not</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Ödeme notu..."
                rows={2}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
                İptal
              </Button>
              <Button onClick={handleAddPayment} disabled={savingPayment}>
                {savingPayment && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Ekle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Düzenleme Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Müşteri Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Salon ve Tarih */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Salon</Label>
                <Select
                  value={editForm.venue_id}
                  onValueChange={(value) => {
                    const venue = venues.find(v => v.id === value);
                    setEditForm({...editForm, venue_id: value, venue_name: venue?.name || ''});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Salon seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {venues.map(venue => (
                      <SelectItem key={venue.id} value={venue.id}>
                        {venue.name} ({venue.capacity} kişi)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Düğün Tarihi</Label>
                <Input
                  type="date"
                  value={editForm.event_date}
                  onChange={(e) => setEditForm({...editForm, event_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Başlangıç Saati</Label>
                <Input
                  type="time"
                  value={editForm.event_time_start}
                  onChange={(e) => setEditForm({...editForm, event_time_start: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş Saati</Label>
                <Input
                  type="time"
                  value={editForm.event_time_end}
                  onChange={(e) => setEditForm({...editForm, event_time_end: e.target.value})}
                />
              </div>
            </div>

            {/* Müşteri Bilgileri */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium mb-3 block">Müşteri Bilgileri</Label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gelin Adı *</Label>
                  <Input
                    value={editForm.bride_name}
                    onChange={(e) => setEditForm({...editForm, bride_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Damat Adı *</Label>
                  <Input
                    value={editForm.groom_name}
                    onChange={(e) => setEditForm({...editForm, groom_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cep 1 *</Label>
                  <Input
                    value={editForm.phone1}
                    onChange={(e) => setEditForm({...editForm, phone1: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cep 2</Label>
                  <Input
                    value={editForm.phone2}
                    onChange={(e) => setEditForm({...editForm, phone2: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>E-posta</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Davetli Sayısı</Label>
                  <Input
                    type="number"
                    value={editForm.guest_count}
                    onChange={(e) => setEditForm({...editForm, guest_count: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Hizmetler */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium mb-3 block">Dahil Hizmetler</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Orkestra</span>
                  </div>
                  <Switch
                    checked={editForm.has_orchestra}
                    onCheckedChange={(checked) => setEditForm({...editForm, has_orchestra: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Kamera</span>
                  </div>
                  <Switch
                    checked={editForm.has_camera}
                    onCheckedChange={(checked) => setEditForm({...editForm, has_camera: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Fotoğraf</span>
                  </div>
                  <Switch
                    checked={editForm.has_photography}
                    onCheckedChange={(checked) => setEditForm({...editForm, has_photography: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Wine className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">İçecek</span>
                  </div>
                  <Switch
                    checked={editForm.has_drinks}
                    onCheckedChange={(checked) => setEditForm({...editForm, has_drinks: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Cookie className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Çerez</span>
                  </div>
                  <Switch
                    checked={editForm.has_snacks}
                    onCheckedChange={(checked) => setEditForm({...editForm, has_snacks: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Yemek</span>
                  </div>
                  <Switch
                    checked={editForm.has_food}
                    onCheckedChange={(checked) => setEditForm({...editForm, has_food: checked})}
                  />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Özel İçerik / Ek Hizmetler</Label>
                <Textarea
                  value={editForm.custom_services}
                  onChange={(e) => setEditForm({...editForm, custom_services: e.target.value})}
                  placeholder="Örn: DJ, Işık Sistemi..."
                  rows={2}
                />
              </div>
            </div>

            {/* Ücret */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium mb-3 block">Ücret Bilgileri</Label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Toplam Tutar (TL)</Label>
                  <Input
                    type="number"
                    value={editForm.total_price || ''}
                    onChange={(e) => setEditForm({...editForm, total_price: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kapora (TL)</Label>
                  <Input
                    type="number"
                    value={editForm.deposit_amount || ''}
                    onChange={(e) => setEditForm({...editForm, deposit_amount: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>
            </div>

            {/* Notlar */}
            <div className="space-y-2">
              <Label>Notlar</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                placeholder="Ek notlar..."
                rows={2}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                İptal
              </Button>
              <Button onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Borç Ekle Dialog */}
      <Dialog open={showDebtDialog} onOpenChange={setShowDebtDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Borç Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300">
              <p className="font-medium">⚠️ Dikkat!</p>
              <p>Borç eklemek kalan tutarı artırır. Hatalı ödeme düzeltmesi veya ekstra hizmet için kullanın.</p>
            </div>
            <div className="space-y-2">
              <Label>Tutar (TL) *</Label>
              <Input
                type="number"
                value={debtForm.amount}
                onChange={(e) => setDebtForm({...debtForm, amount: e.target.value})}
                onFocus={(e) => e.target.select()}
                placeholder="Borç tutarı girin"
              />
            </div>
            <div className="space-y-2">
              <Label>Açıklama/Not * (Zorunlu)</Label>
              <Textarea
                value={debtForm.notes}
                onChange={(e) => setDebtForm({...debtForm, notes: e.target.value})}
                placeholder="Örn: Hatalı ödeme düzeltmesi - 5000 TL fazla girilmişti"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Borç eklerken açıklama zorunludur. Neden borç eklendiğini belirtin.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDebtDialog(false)}>
                İptal
              </Button>
              <Button variant="destructive" onClick={handleAddDebt} disabled={savingDebt}>
                {savingDebt && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Borç Ekle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Kayıtları Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              İşlem Geçmişi
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {logsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Henüz işlem kaydı yok
              </p>
            ) : (
              logs.map((log) => (
                <div key={log.id} className={`p-3 rounded-lg border ${
                  log.action === 'debt_added' ? 'bg-red-50 dark:bg-red-900/20 border-red-200' :
                  log.action === 'payment_added' ? 'bg-green-50 dark:bg-green-900/20 border-green-200' :
                  log.action === 'customer_updated' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200' :
                  'bg-muted/50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {log.action === 'debt_added' && <AlertTriangle className="h-4 w-4 text-red-500" />}
                      {log.action === 'payment_added' && <Plus className="h-4 w-4 text-green-500" />}
                      {log.action === 'customer_updated' && <Edit className="h-4 w-4 text-blue-500" />}
                      {log.action === 'customer_created' && <Users className="h-4 w-4 text-primary" />}
                      <span className="font-medium text-sm">
                        {log.action === 'debt_added' && 'Borç Eklendi'}
                        {log.action === 'payment_added' && 'Ödeme Eklendi'}
                        {log.action === 'customer_updated' && 'Müşteri Güncellendi'}
                        {log.action === 'customer_created' && 'Müşteri Oluşturuldu'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm mt-2">{log.details}</p>
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>{log.user_name || 'Sistem'}</span>
                    <span>{formatDateTime(log.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowLogsDialog(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
